/**
 * Datasource Service
 * API methods for datasource management
 */

import { api } from './api';
import { Datasource } from '../types/platform.types';

export interface CreateDatasourcePayload {
  name: string;
  description?: string;
  type: 'salesforce' | 'sap_erp' | 'odoo';
  credentials: Record<string, any>;
  connectionConfig?: Record<string, any>;
  allowedRoles: string[];
  syncEnabled?: boolean;
  syncFrequencyMinutes?: number;
}

export interface UpdateDatasourcePayload {
  name?: string;
  description?: string;
  credentials?: Record<string, any>;
  connectionConfig?: Record<string, any>;
  allowedRoles?: string[];
  syncEnabled?: boolean;
  syncFrequencyMinutes?: number;
}

export interface TestConnectionResult {
  success: boolean;
  message: string;
  metadata?: any;
}

export interface CategorySummary {
  category: string;
  count: number;
  percentage: number;
}

export interface CategorySummaryResponse {
  success: boolean;
  totalDocuments: number;
  categories: CategorySummary[];
}

export interface DocumentInfo {
  id: string;
  filename: string;
  originalFilename: string;
  category: string;
  tags: string[];
  fileSizeBytes: number;
  mimeType: string;
  createdAt: string;
  odooModel?: string;
  odooId?: string;
}

export interface DocumentsResponse {
  success: boolean;
  documents: DocumentInfo[] | Record<string, DocumentInfo[]>;
  total: number;
  groupedBy?: string;
  limit?: number;
  offset?: number;
}

class DatasourceService {
  /**
   * Fetch all available datasources for current user
   * Datasources are filtered by user role on the backend
   */
  async listDatasources(): Promise<Datasource[]> {
    try {
      const response = await api.get<{ success: boolean; datasources: Datasource[] }>(
        '/datasources'
      );
      return response.datasources || [];
    } catch (error) {
      console.error('Failed to fetch datasources:', error);
      return [];
    }
  }

  /**
   * Get a single datasource by ID
   */
  async getDatasource(id: string): Promise<Datasource> {
    const response = await api.get<{ success: boolean; datasource: Datasource }>(
      `/datasources/${id}`
    );
    return response.datasource;
  }

  /**
   * Create a new datasource
   */
  async createDatasource(data: CreateDatasourcePayload): Promise<Datasource> {
    const response = await api.post<{ success: boolean; datasource: Datasource }>(
      '/datasources',
      data
    );
    return response.datasource;
  }

  /**
   * Update an existing datasource
   */
  async updateDatasource(
    id: string,
    data: UpdateDatasourcePayload
  ): Promise<Datasource> {
    const response = await api.put<{ success: boolean; datasource: Datasource }>(
      `/datasources/${id}`,
      data
    );
    return response.datasource;
  }

  /**
   * Delete a datasource (soft delete)
   */
  async deleteDatasource(id: string): Promise<void> {
    await api.delete(`/datasources/${id}`);
  }

  /**
   * Test datasource connection
   */
  async testConnection(datasourceId: string): Promise<TestConnectionResult> {
    try {
      const response = await api.post<TestConnectionResult>(
        `/datasources/${datasourceId}/test`
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error?.message || 'Connection test failed',
      };
    }
  }

  /**
   * Test connection with credentials before creating datasource
   */
  async testConnectionWithCredentials(
    type: string,
    credentials: Record<string, any>
  ): Promise<TestConnectionResult> {
    try {
      const response = await api.post<TestConnectionResult>(
        '/datasources/test-credentials',
        { type, credentials }
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error?.message || 'Connection test failed',
      };
    }
  }

  /**
   * Get category summary for a datasource
   */
  async getCategorySummary(datasourceId: string): Promise<CategorySummaryResponse> {
    try {
      const response = await api.get<CategorySummaryResponse>(
        `/datasources/${datasourceId}/categories`
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch category summary:', error);
      return {
        success: false,
        totalDocuments: 0,
        categories: [],
      };
    }
  }

  /**
   * Get documents for a datasource
   * @param datasourceId - The datasource ID
   * @param options - Query options
   */
  async getDocumentsByDatasource(
    datasourceId: string,
    options?: {
      groupBy?: 'category';
      category?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<DocumentsResponse> {
    try {
      const params = new URLSearchParams();
      if (options?.groupBy) params.append('groupBy', options.groupBy);
      if (options?.category) params.append('category', options.category);
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());

      const queryString = params.toString();
      const url = `/datasources/${datasourceId}/documents${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<DocumentsResponse>(url);
      return response;
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      return {
        success: false,
        documents: [],
        total: 0,
      };
    }
  }

  /**
   * Trigger sync for a datasource
   */
  async syncDatasource(datasourceId: string): Promise<{ success: boolean; message: string; status?: string }> {
    try {
      const response = await api.post<{ success: boolean; message: string; status?: string }>(
        `/datasources/${datasourceId}/sync`,
        {},
        { timeout: 25 * 60 * 1000 } // 25 minutes
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error?.message || 'Sync failed',
      };
    }
  }
}

export const datasourceService = new DatasourceService();
